import { IconChevronDown } from "@tabler/icons-react";
import {
  UseFormRegister,
  FieldError,
  UseFormClearErrors,
  RegisterOptions,
} from "react-hook-form";
import { useEffect, useRef, useState } from "react";

type SelectOption = {
  id?: string | number;
  value: string | number;
  text: string;
};

type Props = {
  name: string;
  label?: string;
  placeholder?: string;
  selectOptions: SelectOption[];
  register?: UseFormRegister<any>;
  validation?: RegisterOptions;
  error?: FieldError;
  className?: string;
  labelClass?: string;
  disabled?: boolean;
  removeDiv?: boolean;
  value?: string[]; // multiple values
  setValue?: (val: string[]) => void;
  onChange?: (val: string[]) => void;
  clearErrors?: UseFormClearErrors<any>;
  other?: any;
};

export default function MultiSelectInput({
  name,
  label,
  placeholder = "Select",
  selectOptions,
  validation,
  error,
  className = "",
  labelClass = "",
  disabled = false,
  removeDiv = false,
  value = [],
  setValue,
  onChange,
  clearErrors,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = () => {
    if (!disabled) setIsOpen((prev) => !prev);
  };

  const handleSelect = (val: string) => {
    let newValue: string[];
    if (value.includes(val)) {
      newValue = value.filter((v) => v !== val);
    } else {
      newValue = [...value, val];
    }

    if (setValue) setValue(newValue);
    if (onChange) onChange(newValue);
    if (newValue.length && clearErrors) clearErrors(name);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedLabels = selectOptions
    .filter((opt) => value.includes(opt.value.toString()))
    .map((opt) => opt.text)
    .join(", ");

  const displayText = selectedLabels || placeholder;

  return (
    <div className={`space-y-1 relative ${isOpen ? "z-20" : "z-10"} ${className}`} ref={containerRef}>
      {label && (
        <label
          htmlFor={name}
          className={`text-[#1E293B] text-xs ${labelClass} whitespace-nowrap`}
        >
          {label}
          {validation?.required && <span className="text-red-600 ml-1">*</span>}
        </label>
      )}

      <div className="relative w-full">
        <button
          type="button"
          disabled={disabled}
          onClick={toggleDropdown}
          className={`w-full text-left text-xs px-3 py-[10px] rounded-xl appearance-none bg-white text-[#1E1E1E]
            border ${error ? "border-red-500" : "border-gray-200"}
            focus:outline-none  
            ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
        >
          <span className=" "> {displayText}</span>
        </button>

        <IconChevronDown
          size={18}
          stroke={2.2}
          className={`absolute right-3 top-2/4 transform -translate-y-1/2 text-gray-500 transition-transform ${
            isOpen ? "rotate-180" : ""
          } ${disabled ? "opacity-40" : ""}`}
        />

        {isOpen && (
          <div className="absolute z-10 mt-1 w-full max-h-52 overflow-auto rounded-md border border-gray-200 bg-white shadow-sm text-xs">
            {selectOptions.length > 0 ? (
              selectOptions.map((opt, i) => (
                <label
                  key={opt.id ?? i}
                  className="flex items-center gap-2 px-4 py-2 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={value?.includes(opt?.value.toString())}
                    onChange={() => handleSelect(opt?.value.toString())}
                    className="accent-[#FBD037] w-4 h-4"
                    disabled={disabled}
                  />
                  <span>{opt.text}</span>
                </label>
              ))
            ) : (
              <div className="px-4 py-3 text-gray-400 text-center">No options available</div>
            )}
          </div>
        )}
      </div>

      <span className="text-red-500 text-xs block pl-3 ">
        {error?.message || ""}
      </span>
    </div>
  );
}
